// Library methods
import { useCallback, useMemo } from "react";

// Components
import Box from "@mui/material/Box";
import Circle from "src/assets/shapes/Circle/Circle";
import OcularMotilityLegend from "./OcularMotilityLegend/OcularMotilityLegend";

// Types
import { ExamSectionReportType } from "src/services/exams/types";

const DEFAULT_CONTAINER_WIDTH = 304;
const DEFAULT_CONTAINER_HEIGHT = 224;

// `containerSize` is the full width of the surrounding container (legend,
// padding, and all) -- only a fraction of that should actually go toward
// the diagram itself, and MAX_SCALE_FACTOR keeps it from ballooning on
// very wide desktop screens.
const CONTAINER_SIZE_RATIO = 0.5;
const MAX_SCALE_FACTOR = 1.5;

type OcularMotilityType = {
  examData: ExamSectionReportType;
  containerSize?: number;
};

const OcularMotility = ({ examData, containerSize }: OcularMotilityType) => {
  // Scale up from the 304x224 default when more space is available, but
  // never below it -- anything smaller than the default just keeps scale
  // at 1, and anything larger is capped at MAX_SCALE_FACTOR.
  const scaleFactor = useMemo(() => {
    if (!containerSize) return 1;

    const rawScale =
      (containerSize * CONTAINER_SIZE_RATIO) / DEFAULT_CONTAINER_WIDTH;

    return Math.min(Math.max(rawScale, 1), MAX_SCALE_FACTOR);
  }, [containerSize]);

  const width = useMemo(
    () => DEFAULT_CONTAINER_WIDTH * scaleFactor,
    [scaleFactor]
  );
  const height = useMemo(
    () => DEFAULT_CONTAINER_HEIGHT * scaleFactor,
    [scaleFactor]
  );
  const pointSize = useMemo(() => 15 * scaleFactor, [scaleFactor]);

  const calculateXPosition = useCallback(
    (xVal: number) => {
      const xFloor = Math.floor(xVal);
      const pointWidth = 7.5 * scaleFactor;

      if (xFloor < 0) {
        const valToReturn = -4 * scaleFactor * xFloor;
        return width / 2 - valToReturn - pointWidth;
      } else if (xFloor === 0) {
        return width / 2 - pointWidth;
      } else {
        const valToReturn = width / 2 + 4 * scaleFactor * xFloor;
        return valToReturn - pointWidth;
      }
    },
    [scaleFactor, width]
  );

  const calculateYPosition = useCallback(
    (yVal: number) => {
      const yFloor = Math.floor(yVal);
      const pointWidth = 7.5 * scaleFactor;

      if (yFloor < 0) {
        const valToReturn = height / 2 + -4 * scaleFactor * yFloor;
        return valToReturn - pointWidth;
      } else if (yFloor === 0) {
        return height / 2 - pointWidth;
      } else {
        const valToReturn = height / 2 - 4 * scaleFactor * yFloor;
        return valToReturn - pointWidth;
      }
    },
    [scaleFactor, height]
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "20px",
      }}
    >
      <OcularMotilityLegend />

      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <main
          style={{
            width: `${width}px`,
            height: `${height}px`,
            position: "relative",
          }}
        >
          {/* Inactive Focal Points */}
          {examData?.state?.focalPointPositions?.map((focalPoint, index) => (
            <div
              key={`focal-point-${index}`}
              style={{
                position: "absolute",
                paddingLeft: calculateXPosition(focalPoint?.Item1),
                paddingTop: calculateYPosition(focalPoint?.Item2),
              }}
            >
              <Circle size={pointSize} stroke="#5B6D7A70" />
            </div>
          ))}

          {/* Current Focal Point */}
          {examData?.state?.eyeData && (
            <div
              key="current-focal-point"
              style={{
                position: "absolute",
                paddingLeft: calculateXPosition(
                  examData?.state?.eyeData?.focalPointAngleX
                ),
                paddingTop: calculateYPosition(
                  examData?.state?.eyeData?.focalPointAngleY
                ),
              }}
            >
              <Circle size={pointSize} />
            </div>
          )}

          {/* Left Eye */}
          {examData?.state?.eyeData && (
            <div
              key="left-eye"
              style={{
                position: "absolute",
                paddingLeft: calculateXPosition(
                  examData?.state?.eyeData?.leftEyeAngleX
                ),
                paddingTop: calculateYPosition(
                  examData?.state?.eyeData?.leftEyeAngleY
                ),
              }}
            >
              <Circle size={pointSize} fill="red" opacity={0.5} />
            </div>
          )}

          {/* Right Eye */}
          {examData?.state?.eyeData && (
            <div
              key="right-eye"
              style={{
                position: "absolute",
                paddingLeft: calculateXPosition(
                  examData?.state?.eyeData?.rightEyeAngleX
                ),
                paddingTop: calculateYPosition(
                  examData?.state?.eyeData?.rightEyeAngleY
                ),
              }}
            >
              <Circle size={pointSize} fill="blue" opacity={0.5} />
            </div>
          )}
        </main>
      </Box>
    </Box>
  );
};

export default OcularMotility;
